import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, ExternalLink, TriangleAlert } from "lucide-react";

import { EmptyPanel } from "@/components/interior-page";
import Loader from "@/components/ui/loader";
import { ApiError } from "@/lib/api";
import {
  NEWS_FALLBACK_THUMBNAIL,
  fetchResource,
  formatPublishedDate,
  type ResourceContent,
} from "@/lib/resources";

const NEWS_LIST_PATH = "/resources/news";

/**
 * Internal article detail page for a news item with no external URL.
 *
 * Reached from the "Read More" action on the news grid, which keeps its cards a
 * fixed size instead of expanding content inline. The API re-checks role access
 * by id, so editing the URL can't surface an article this role isn't mapped to
 * — a 404 from the server is rendered as "not available" here.
 */
export default function NewsArticlePage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [article, setArticle] = useState<ResourceContent | null>(null);
  const [error, setError] = useState<"missing" | "failed" | null>(null);

  useEffect(() => {
    setArticle(null);
    setError(null);
    fetchResource(id)
      .then(setArticle)
      .catch((err) => setError(err instanceof ApiError && err.status === 404 ? "missing" : "failed"));
  }, [id]);

  // Browser-style back keeps the list's scroll position. Falls back to a plain
  // navigation when the page was opened directly (no in-app history to pop).
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate(NEWS_LIST_PATH);
  };

  return (
    <div className="public-stage pb-8">
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        {/* Full available width — no max-width and no mx-auto, matching the
            Home page (shellWidth = "w-full" in pages/Home.tsx) and every other
            Resource page. Padding comes from the wrapper above: px-4 (16px) /
            sm:px-6 (24px) / lg:px-8 (32px), the app-wide page gutters.
            Only the prose paragraphs below carry a readable-measure cap. */}
        <div className="w-full">
          <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>Resource</span>
            <span className="text-muted-foreground/40">/</span>
            <Link to={NEWS_LIST_PATH} className="hover:text-foreground">
              Latest News &amp; Articles
            </Link>
            <span className="text-muted-foreground/40">/</span>
            {/* min-w-0 lets the flex item shrink below its content width, so a
                long title truncates instead of pushing the row wide. */}
            <span className="min-w-0 truncate font-semibold text-primary">
              {article?.title ?? "Article"}
            </span>
          </nav>

          <button
            type="button"
            onClick={goBack}
            className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary underline-offset-2 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Latest News &amp; Articles
          </button>

          {error ? (
            <EmptyPanel
              icon={TriangleAlert}
              title={error === "missing" ? "Article not available" : "Couldn't load this article"}
              description={
                error === "missing"
                  ? "This article may have been unpublished, or it isn't available for your role."
                  : "Something went wrong fetching this article. Please refresh the page to try again."
              }
            />
          ) : !article ? (
            <div className="flex justify-center py-12"><Loader /></div>
          ) : (
            <article>
              {article.category && (
                <span className="inline-block text-[0.65rem] font-bold uppercase tracking-wide text-primary">
                  {article.category}
                </span>
              )}
              {/* break-words so an unbroken string (a long URL, a run-on token)
                  wraps instead of forcing the page wide. */}
              <h1 className="mt-1 break-words font-display text-2xl font-extrabold leading-tight text-foreground sm:text-3xl">
                {article.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {formatPublishedDate(article.published_at)}
                </span>
                {article.external_url && (
                  <a
                    href={article.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                  >
                    Original source
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>

              {/* Full width, but a capped height per breakpoint rather than a
                  16:9 ratio — at this column width 16:9 made the hero far too
                  tall. object-cover crops to fit, so nothing stretches.
                  224px mobile / 288px tablet / 360px desktop. */}
              <div className="mt-5 h-56 w-full overflow-hidden rounded-2xl border border-border bg-muted sm:h-72 lg:h-[360px]">
                {/* object-position: center 30% — biases the crop window upward
                    so faces/subjects near the top of the frame stay visible at
                    this hero height. Still object-cover, so the container is
                    always filled with no letterboxing. Detail-page hero only;
                    the listing thumbnails keep object-center. */}
                <img
                  src={article.thumbnail_url || NEWS_FALLBACK_THUMBNAIL}
                  alt=""
                  className="block h-full w-full object-cover object-[center_30%]"
                  onError={(e) => {
                    if (e.currentTarget.src.endsWith(NEWS_FALLBACK_THUMBNAIL)) return;
                    e.currentTarget.src = NEWS_FALLBACK_THUMBNAIL;
                  }}
                />
              </div>

              {/* Standfirst: the existing short description, set bold and at full
                  foreground contrast so it reads as a lead-in rather than body
                  copy. Detail-page only — the listing card keeps its own styling.
                  Not rendered at all when empty, so no stray whitespace. */}
              {article.short_description && (
                <p className="mt-6 break-words text-base font-semibold leading-7 text-foreground sm:text-lg">
                  {article.short_description}
                </p>
              )}

              {/* No max-width: the prose shares the hero's exact left and right
                  boundaries. Line-height, spacing and font sizes are unchanged. */}
              {article.body && (
                <p className="mt-5 whitespace-pre-wrap break-words text-sm leading-7 text-muted-foreground sm:text-base">
                  {article.body}
                </p>
              )}
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
